import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Drawer, Typography } from '@mui/material';
import RoleTopBar from './RoleTopBar';
import { useRoleNav } from '../../context/RoleNavContext';
import { roleColors } from '../../theme/roleMockup';
import { isTesterKycApproved } from '../../utils/testerKyc';
import TesterKycBanner from '../tester/TesterKycBanner';
import TesterProfileIncompleteBanner from '../tester/TesterProfileIncompleteBanner';
import PwaInstallBanner from '../tester/PwaInstallBanner';
import TesterChatNotifications, {
  isChatNotifyBannerEligible,
} from '../tester/TesterChatNotifications';
import ImpersonationBanner from '../common/ImpersonationBanner';
import { getTesterPaymentEligibilityGaps } from '../../utils/testerPaymentEligibility';
import {
  isIncompleteProfilePathAllowed,
  isNavLockedForIncompleteProfile,
} from '../../utils/testerIncompleteAccess';

const TESTER_NAV = [
  {
    section: 'Overview',
    items: [
      { label: 'Dashboard', icon: '🏠', path: '/app/dashboard', mobileLabel: 'Home' },
      { label: 'Open Cycles', icon: '🛒', path: '/app/open-cycles', badgeKey: 'openCycles', badgeColor: 'green', mobileLabel: 'Open' },
      { label: 'Chat with Support', icon: '💬', path: '/app/messages', badgeKey: 'unreadMessages', badgeColor: 'blue', mobileLabel: 'Support' },
    ],
  },
  {
    section: 'My Work',
    items: [
      { label: 'My Cycles', icon: '🔄', path: '/app/test-cycles', badgeKey: 'myCycles', badgeColor: 'blue', mobileLabel: 'Cycles' },
      { label: 'Report defect', icon: '🐛', path: null, action: 'reportBug', mobileLabel: 'Defect' },
      { label: 'My Issues', icon: '📝', path: '/app/my-reported-issues', mobileLabel: 'Issues' },
    ],
  },
  {
    section: 'Growth',
    items: [
      { label: 'Earnings', icon: '💰', path: '/app/earnings', mobileLabel: 'Earnings' },
      { label: 'Academy', icon: '🎓', path: '/app/academy', mobileLabel: 'Academy' },
      { label: 'User Guide', icon: '📘', path: '/app/user-guide', mobileLabel: 'Guide' },
      { label: 'My Profile', icon: '⭐', path: '/app/tester-profile', mobileLabel: 'Profile' },
    ],
  },
];


function NavItem({ item, active, badge, onClick, disabled, title }) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      title={title}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        py: '9px',
        px: '14px',
        mx: 1,
        borderRadius: '6px',
        fontSize: 12.5,
        fontWeight: active ? 600 : 500,
        color: active ? roleColors.activeNavText : disabled ? '#cbd5e1' : '#64748b',
        bgcolor: active ? roleColors.activeNavBg : 'transparent',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.65 : 1,
        transition: 'all 0.12s',
        '&:hover': disabled
          ? {}
          : {
              bgcolor: active ? roleColors.activeNavBg : '#f1f5f9',
              color: active ? roleColors.activeNavText : '#1e293b',
            },
      }}
    >
      <Box component="span" sx={{ fontSize: 15, width: 18, textAlign: 'center' }}>
        {item.icon}
      </Box>
      {item.label}
      {badge != null && badge > 0 && (
        <Box
          component="span"
          sx={{
            ml: 'auto',
            fontSize: 10,
            fontWeight: 700,
            px: '6px',
            py: '1px',
            borderRadius: '10px',
            bgcolor: item.badgeColor === 'green' ? '#10b981' : '#3b82f6',
            color: '#fff',
          }}
        >
          {badge}
        </Box>
      )}
    </Box>
  );
}

export default function TesterShellLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { badges, getReportBugPath } = useRoleNav();
  const { user } = useSelector((state) => state.auth);
  const kycApproved = isTesterKycApproved(user);
  const paymentGaps = useMemo(() => getTesterPaymentEligibilityGaps(user), [user]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [bannerTick, setBannerTick] = useState(0);

  const profileCompleted = Boolean(user?.profileCompleted);
  const isDashboard =
    location.pathname === '/app/dashboard' || location.pathname === '/dashboard';

  // At most one shell promo banner (Home owns the incomplete-profile CTA).
  const [shellBanner, setShellBanner] = useState(null);
  useEffect(() => {
    if (!profileCompleted) {
      setShellBanner(isDashboard ? null : 'profile');
      return;
    }
    if (!kycApproved) {
      setShellBanner('kyc');
      return;
    }
    if (isChatNotifyBannerEligible()) {
      setShellBanner('chat');
      return;
    }
    setShellBanner('pwa');
  }, [profileCompleted, kycApproved, isDashboard, bannerTick]);

  useEffect(() => {
    if (user?.role !== 'tester') return;
    if (!profileCompleted) {
      if (!isIncompleteProfilePathAllowed(location.pathname)) {
        navigate('/app/dashboard', { replace: true });
      }
      return;
    }
    if (!paymentGaps.needsModal) return;
    if (location.pathname === '/app/payout-compliance' || location.pathname === '/app/settings') return;
    navigate('/app/payout-compliance', { replace: true });
  }, [user?.role, profileCompleted, paymentGaps.needsModal, location.pathname, navigate]);

  const isActive = (path) => {
    if (!path) return false;
    if (path === '/app/dashboard') {
      return location.pathname === '/app/dashboard' || location.pathname === '/dashboard';
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isNavBlocked = (item) => {
    if (!profileCompleted && isNavLockedForIncompleteProfile(item)) return true;
    if (item.action === 'reportBug' && !kycApproved) return true;
    return false;
  };

  const navBlockReason = (item) => {
    if (!profileCompleted && isNavLockedForIncompleteProfile(item)) {
      return 'Complete your profile to unlock';
    }
    if (item.action === 'reportBug' && !kycApproved) {
      return 'Identity verification required';
    }
    return undefined;
  };

  const handleNav = (item) => {
    // A blocked item keeps the drawer open so the reason stays on screen.
    if (isNavBlocked(item)) return;
    setMobileNavOpen(false);
    if (item.action === 'reportBug') {
      navigate(getReportBugPath());
      return;
    }
    if (item.path) navigate(item.path);
  };

  /**
   * One nav definition, rendered twice: as the fixed rail on desktop and inside
   * the mobile drawer. Keeping a single source stops the two from drifting.
   */
  const renderNavBody = () => (
    <>
            {TESTER_NAV.map((group) => (
              <Box key={group.section}>
                <Typography
                  sx={{
                    px: 1.5,
                    pt: group.section === 'Overview' ? 1.5 : 1,
                    pb: 0.5,
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: '#94a3b8',
                  }}
                >
                  {group.section}
                </Typography>
                {group.items.map((item) => {
                  const blocked = isNavBlocked(item);
                  const reason = navBlockReason(item);
                  let label = item.label;
                  if (blocked && !profileCompleted && isNavLockedForIncompleteProfile(item)) {
                    label = `${item.label} (profile)`;
                  } else if (item.action === 'reportBug' && !kycApproved && profileCompleted) {
                    label = `${item.label} (Identity verification required)`;
                  }
                  return (
                    <NavItem
                      key={item.label}
                      item={{ ...item, label }}
                      active={item.path ? isActive(item.path) : false}
                      badge={item.badgeKey ? badges[item.badgeKey] : null}
                      disabled={blocked}
                      title={reason}
                      onClick={() => handleNav(item)}
                    />
                  );
                })}
              </Box>
            ))}
      <Box sx={{ mt: 'auto', pt: 1, pb: 1.5, borderTop: `1px solid ${roleColors.cardBorder}` }}>
        <NavItem
          item={{ label: 'Settings', icon: '⚙️', path: '/app/settings' }}
          active={isActive('/app/settings')}
          badge={null}
          onClick={() => {
            setMobileNavOpen(false);
            navigate('/app/settings');
          }}
        />
      </Box>
    </>
  );

  return (
    <Box sx={{ minHeight: '100dvh', height: '100dvh', display: 'flex', flexDirection: 'column', bgcolor: roleColors.pageBg }}>
      <ImpersonationBanner />
      <RoleTopBar onMenuClick={() => setMobileNavOpen(true)} />
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Box
          component="nav"
          sx={{
            width: 220,
            flexShrink: 0,
            bgcolor: '#fff',
            borderRight: `1px solid ${roleColors.cardBorder}`,
            overflowY: 'auto',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
          }}
        >
          {renderNavBody()}
        </Box>

        {/* Mobile: the full menu in a drawer. A bottom bar only had room for
            three destinations and buried the rest behind "More"; every item is
            now one tap from the header. */}
        <Drawer
          variant="temporary"
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 260,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#fff',
            },
          }}
        >
          {renderNavBody()}
        </Drawer>

        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            p: { xs: 1.25, sm: 2, md: 3 },
            pb: { xs: 'calc(1.25rem + env(safe-area-inset-bottom))', md: 3 },
            bgcolor: roleColors.pageBg,
            scrollBehavior: 'smooth',
          }}
        >
          {/* Socket listener always mounted; UI only when this banner wins priority */}
          <TesterChatNotifications
            forceShow={shellBanner === 'chat'}
            onDismiss={() => setBannerTick((t) => t + 1)}
          />
          {shellBanner === 'profile' && <TesterProfileIncompleteBanner />}
          {shellBanner === 'kyc' && <TesterKycBanner />}
          <PwaInstallBanner forceShow={shellBanner === 'pwa'} />
          {children || <Outlet />}
        </Box>
      </Box>
    </Box>
  );
}
